import React from 'react';
import Icon from '../../../components/AppIcon';

const TrustIndicators = () => {
  const testimonials = [
    {
      name: 'Mike Rodriguez',
      business: 'Rodriguez Construction',
      rating: 5,
      text: 'Stackd has transformed how I find and manage clients. I\'ve increased my revenue by 200% in just 8 months.',
      projectsCompleted: 47,
      specialty: 'Home Renovation'
    },
    {
      name: 'Sarah Chen',
      business: 'Elite Landscaping',
      rating: 5,
      text: 'The platform makes it so easy to connect with quality clients. Payment processing is seamless and fast.',
      projectsCompleted: 62,
      specialty: 'Landscape Design'
    },
    {
      name: 'James Wilson',
      business: 'Wilson Electrical Services',
      rating: 5,
      text: 'Best decision I made for my business. The quote management tools alone save me 10+ hours per week.',
      projectsCompleted: 89,
      specialty: 'Electrical Work'
    }
  ];

  const platformStats = [
    {
      icon: 'Users',
      value: '25,000+',
      label: 'Verified Vendors',
      description: 'Trusted professionals nationwide'
    },
    {
      icon: 'Briefcase',
      value: '$150M+',
      label: 'Projects Completed',
      description: 'Successfully delivered through our platform'
    },
    {
      icon: 'Star',
      value: '4.8/5',
      label: 'Platform Rating',
      description: 'Based on vendor and client feedback'
    },
    {
      icon: 'Shield',
      value: '99.9%',
      label: 'Payment Success',
      description: 'Secure and guaranteed payments'
    }
  ];

  const securityFeatures = [
    {
      icon: 'Shield',
      title: 'Background Verification',
      description: 'All vendors undergo thorough background checks and license validation'
    },
    {
      icon: 'Lock',
      title: 'Secure Payments',
      description: 'Bank-level encryption and PCI DSS compliance for all transactions'
    },
    {
      icon: 'FileCheck',
      title: 'Insurance Verified',
      description: 'Insurance coverage verification for all registered vendors'
    },
    {
      icon: 'Award',
      title: 'Quality Assurance',
      description: 'Continuous monitoring and quality assessment of all projects'
    }
  ];

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={16}
        className={i < rating ? 'text-warning fill-current' : 'text-muted'}
      />
    ));
  };

  return (
    <section className="py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Trusted by Thousands of Vendors
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Join a community of successful vendors who trust Stackd with their business growth
          </p>
        </div>

        {/* Platform Statistics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {platformStats?.map((stat, index) => (
            <div key={index} className="bg-card border border-border rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Icon name={stat?.icon} size={24} className="text-primary" />
              </div>
              <div className="text-3xl font-bold text-foreground mb-2">{stat?.value}</div>
              <div className="text-lg font-semibold text-foreground mb-1">{stat?.label}</div>
              <div className="text-sm text-text-secondary">{stat?.description}</div>
            </div>
          ))}
        </div>

        {/* Vendor Testimonials */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">
            What Our Vendors Say
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials?.map((testimonial, index) => (
              <div key={index} className="bg-card border border-border rounded-xl p-6">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">
                      {testimonial?.name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground">{testimonial?.name}</h4>
                    <p className="text-sm text-text-secondary">{testimonial?.business}</p>
                  </div>
                </div>

                <div className="flex items-center mb-3">
                  {renderStars(testimonial?.rating)}
                </div>

                <p className="text-text-secondary mb-4 italic">
                  "{testimonial?.text}"
                </p>

                <div className="flex items-center justify-between text-sm">
                  <div className="text-text-secondary">
                    <span className="font-semibold text-primary">{testimonial?.projectsCompleted}</span> projects completed
                  </div>
                  <div className="text-text-secondary">
                    {testimonial?.specialty}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Security and Trust Features */}
        <div className="bg-card border border-border rounded-2xl p-8">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-foreground mb-2">
              Your Security is Our Priority
            </h3>
            <p className="text-text-secondary">
              We maintain the highest standards of security and verification
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityFeatures?.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={feature?.icon} size={32} className="text-success" />
                </div>
                <h4 className="font-semibold text-foreground mb-2">{feature?.title}</h4>
                <p className="text-sm text-text-secondary">{feature?.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center items-center gap-8 mt-12 pt-8 border-t border-border">
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="Shield" size={20} className="text-success" />
            <span className="text-sm">SSL Secured</span>
          </div>
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="Award" size={20} className="text-primary" />
            <span className="text-sm">Better Business Bureau A+</span>
          </div>
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <span className="text-sm">GDPR Compliant</span>
          </div>
          <div className="flex items-center space-x-2 text-text-secondary">
            <Icon name="Lock" size={20} className="text-warning" />
            <span className="text-sm">PCI DSS Certified</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustIndicators;